const pluraliseDays = (days: number): string => `${String(days)} day${days === 1 ? '' : 's'}`;

export const planCopy = {
  alerts: {
    memberAccessLocked:
      'Solo only allows the earliest member to manage uploads and settings. You still have read access.',
    memberGrace: ({ remainingDays }: { remainingDays: number }) =>
      `Solo keeps write access for the earliest member only. Your write access ends in ${pluraliseDays(
        remainingDays,
      )}.`,
    billingPastDue: ({ retryLabel }: { retryLabel: string | null }) =>
      retryLabel === null
        ? 'Payment failed. Update billing details to avoid upload restrictions.'
        : `Payment failed. Stripe will retry on ${retryLabel}. Update billing details to avoid upload restrictions.`,
    billingUploadLocked:
      'Uploads are disabled until the outstanding subscription payment is resolved. Existing data remains available.',
  },
  badges: {
    billing: 'Billing',
    grace: 'Grace',
    locked: 'Locked',
    usage80: '80% Used',
  },
} as const;

export type PlanAlertCopyKey = keyof typeof planCopy.alerts;
export type PlanBadgeCopyKey = keyof typeof planCopy.badges;

export type PlanAlertCopyArgs<K extends PlanAlertCopyKey> = (typeof planCopy.alerts)[K] extends (
  args: infer TArgs,
) => string
  ? TArgs
  : undefined;

export type PlanBadgeCopyArgs<K extends PlanBadgeCopyKey> = (typeof planCopy.badges)[K] extends (
  args: infer TArgs,
) => string
  ? TArgs
  : undefined;

export const getPlanAlertCopy = <K extends PlanAlertCopyKey>(
  key: K,
  args?: PlanAlertCopyArgs<K>,
): string => {
  const entry = planCopy.alerts[key];
  if (typeof entry === 'function') {
    return (entry as (messageArgs: PlanAlertCopyArgs<K>) => string)(
      args ?? ({} as PlanAlertCopyArgs<K>),
    );
  }
  return entry;
};

export const getPlanBadgeCopy = <K extends PlanBadgeCopyKey>(
  key: K,
  args?: PlanBadgeCopyArgs<K>,
): string => {
  const entry = planCopy.badges[key];
  if (typeof entry === 'function') {
    return (entry as (messageArgs: PlanBadgeCopyArgs<K>) => string)(
      args ?? ({} as PlanBadgeCopyArgs<K>),
    );
  }
  return entry;
};
